import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import publicCta from '../../content/publicCta.json';

export default function NewsListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get('/v1/public/noticias', { params: { todas: '1' } });
        if (cancelled) return;
        const raw = res?.data;
        setItems(Array.isArray(raw) ? raw : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudieron cargar las noticias');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-[60vh] pb-16 lg:pb-24">
      <PublicPageHero
        eyebrow="Asociados"
        title="Todas las noticias"
        description="Comunicados y recordatorios publicados para asociados. Solo se muestran noticias activas en el portal."
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </p>
        )}

        <div className={error ? 'mt-10' : ''}>
          {loading && (
            <BrandLoading variant="inline" tone="light" label="Cargando noticias…" className="min-h-[40vh]" />
          )}

          {!loading && !error && items.length === 0 && (
            <p className="text-center text-slate-600">No hay noticias publicadas por ahora.</p>
          )}

          {!loading && items.length > 0 && (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((n, i) => (
              <ScrollReveal key={n.id} delayMs={Math.min(i, 8) * 40} className="min-w-0">
                <Link
                  to={`/noticias/${n.id}`}
                  className="group block rounded-2xl no-underline text-inherit outline-none focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  aria-label={`${n.titulo}: ver detalle de la noticia`}
                >
                  <PremiumCard hover className="flex h-full cursor-pointer flex-col">
                    <div className="relative mb-5 aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
                      {n.imagen ? (
                        <img
                          src={assetUrl(n.imagen)}
                          alt=""
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs font-medium uppercase tracking-wider text-slate-500">
                          Sin imagen
                        </div>
                      )}
                      <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
                        {n.categoria}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Calendar className="h-3.5 w-3.5 text-accent-lime" aria-hidden />
                        {new Date(n.fecha_publicacion).toLocaleDateString('es-CO', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                      <h2 className="mt-3 font-display text-xl font-semibold text-white">{n.titulo}</h2>
                      <p className="mt-2 line-clamp-3 flex-1 text-base leading-relaxed text-slate-400">{n.resumen}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition group-hover:text-white">
                        {publicCta.newsCardLinkLabel} <ChevronRight className="h-4 w-4" aria-hidden />
                      </span>
                    </div>
                  </PremiumCard>
                </Link>
              </ScrollReveal>
              ))}
            </div>
          )}
        </div>
        </div>
      </section>
    </div>
  );
}
